import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import BaseModal from "./BaseModal";
import { Badge } from "../../components/ui";
import { addCard } from "../../services/paymentStorage";

function onlyDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function formatCardNumber(value) {
  const digits = onlyDigits(value).slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

function formatExpiry(value) {
  const digits = onlyDigits(value).slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

function inferBrand(cardNumberDigits) {
  if (cardNumberDigits.startsWith("8600")) return "uzcard";
  if (cardNumberDigits.startsWith("9860")) return "humo";
  return "card";
}

function validateExpiry(mmYY) {
  const digits = onlyDigits(mmYY);
  if (digits.length !== 4) return { ok: false, reason: "format" };
  const mm = Number(digits.slice(0, 2));
  const yy = Number(digits.slice(2));
  if (mm < 1 || mm > 12) return { ok: false, reason: "month" };
  const now = new Date();
  const currentYY = Number(String(now.getFullYear()).slice(-2));
  const currentMM = now.getMonth() + 1;
  if (yy < currentYY || (yy === currentYY && mm < currentMM)) return { ok: false, reason: "expired" };
  return { ok: true };
}

export default function AddCardModal({ onClose, onSuccess }) {
  const { t } = useTranslation();
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [holder, setHolder] = useState("");
  const [error, setError] = useState("");

  const cardDigits = useMemo(() => onlyDigits(cardNumber), [cardNumber]);
  const brand = useMemo(() => inferBrand(cardDigits), [cardDigits]);

  const onSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (cardDigits.length !== 16) {
      setError(t("billing.addCard.errors.cardNumber"));
      return;
    }
    const expiryCheck = validateExpiry(expiry);
    if (!expiryCheck.ok) {
      setError(t("billing.addCard.errors.expiry"));
      return;
    }
    const cvvDigits = onlyDigits(cvv);
    if (cvvDigits.length !== 3) {
      setError(t("billing.addCard.errors.cvv"));
      return;
    }
    const name = String(holder || "").trim();
    if (name.length < 3) {
      setError(t("billing.addCard.errors.holder"));
      return;
    }

    const last4 = cardDigits.slice(-4);
    const nextCards = addCard({
      brand,
      last4,
      holder: name,
    });
    const created = nextCards[0];
    onSuccess?.(created);
    onClose?.();
  };

  return (
    <BaseModal
      title={t("billing.addCard.title")}
      onClose={onClose}
      maxWidthClass="max-w-xl"
    >
      <div className="space-y-10 py-4 px-2">
        <div className="relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-[#1e9f6b]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 p-8 bg-slate-900/40 dark:bg-black/40 rounded-[32px] border border-white/5 backdrop-blur-xl relative z-10">
            <div className="space-y-2">
              <h4 className="text-xl font-black text-white italic uppercase tracking-tighter">
                {t("billing.addCard.subtitle")}
              </h4>
              <p className="text-[10px] font-bold text-white/40 uppercase tracking-[0.3em]">{t("billing.addCard.pciNote")}</p>
            </div>
            <div className="flex gap-3">
              <motion.div 
                animate={{ 
                  scale: brand === 'uzcard' ? 1.1 : 1,
                  opacity: brand === 'uzcard' || brand === 'card' ? 1 : 0.2 
                }}
                className={`w-14 h-9 rounded-xl flex items-center justify-center overflow-hidden shadow-2xl transition-all duration-500 ${
                  brand === 'uzcard' ? 'ring-2 ring-[#1e9f6b] ring-offset-4 ring-offset-[#020617]' : ''
                } bg-[#005696]`}
              >
                <span className="text-[10px] font-black text-white italic tracking-tighter">UZCARD</span>
              </motion.div>
              <motion.div 
                animate={{ 
                  scale: brand === 'humo' ? 1.1 : 1,
                  opacity: brand === 'humo' || brand === 'card' ? 1 : 0.2 
                }}
                className={`w-14 h-9 rounded-xl flex items-center justify-center overflow-hidden shadow-2xl transition-all duration-500 ${
                  brand === 'humo' ? 'ring-2 ring-[#1e9f6b] ring-offset-4 ring-offset-[#020617]' : ''
                } bg-[#f7931e]`}
              >
                <span className="text-[10px] font-black text-white italic tracking-tighter">HUMO</span>
              </motion.div>
            </div>
          </div>
        </div>

        <form id="add-card-form" className="space-y-8" onSubmit={onSubmit}>
          {/* Card Number */}
          <div className="space-y-3">
            <label className="text-[11px] font-black text-white/30 uppercase tracking-[0.4em] px-2">
              {t("billing.addCard.fields.cardNumber")}
            </label>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-[#1e9f6b]/5 to-transparent rounded-[28px] opacity-0 group-focus-within:opacity-100 transition-opacity duration-500" />
              <span className="material-symbols-outlined absolute left-6 top-1/2 -translate-y-1/2 text-white/20 text-[24px] transition-all group-focus-within:text-[#1e9f6b] group-focus-within:scale-110">credit_card</span>
              <input
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                inputMode="numeric"
                autoComplete="cc-number"
                placeholder={t("billing.addCard.placeholders.cardNumber")}
                className="w-full h-[76px] bg-white/[0.03] dark:bg-black/20 border-2 border-white/5 focus:border-[#1e9f6b] rounded-[28px] pl-16 pr-6 text-2xl font-black text-white outline-none transition-all italic tracking-[0.1em] placeholder:text-white/10"
              />
              <div className="absolute right-6 top-1/2 -translate-y-1/2">
                <Badge variant={brand === 'card' ? 'slate' : 'emerald'} className="font-black italic uppercase tracking-widest text-[9px]">
                  {brand}
                </Badge>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8">
            {/* Expiry */}
            <div className="space-y-3">
              <label className="text-[11px] font-black text-white/30 uppercase tracking-[0.4em] px-2">
                {t("billing.addCard.fields.expiry")}
              </label>
              <div className="relative group">
                <span className="material-symbols-outlined absolute left-6 top-1/2 -translate-y-1/2 text-white/20 text-[24px] transition-all group-focus-within:text-[#1e9f6b]">calendar_month</span>
                <input
                  value={expiry}
                  onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                  inputMode="numeric"
                  autoComplete="cc-exp"
                  placeholder={t("billing.addCard.placeholders.expiry")}
                  className="w-full h-[76px] bg-white/[0.03] dark:bg-black/20 border-2 border-white/5 focus:border-[#1e9f6b] rounded-[28px] pl-16 pr-6 text-xl font-black text-white outline-none transition-all italic placeholder:text-white/10"
                />
              </div>
            </div>
            {/* CVV */}
            <div className="space-y-3">
              <label className="text-[11px] font-black text-white/30 uppercase tracking-[0.4em] px-2">
                {t("billing.addCard.fields.cvv")}
              </label>
              <div className="relative group">
                <span className="material-symbols-outlined absolute left-6 top-1/2 -translate-y-1/2 text-white/20 text-[24px] transition-all group-focus-within:text-[#1e9f6b]">lock</span>
                <input
                  value={cvv}
                  onChange={(e) => setCvv(onlyDigits(e.target.value).slice(0, 3))}
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  type="password"
                  placeholder={t("billing.addCard.placeholders.cvv")}
                  className="w-full h-[76px] bg-white/[0.03] dark:bg-black/20 border-2 border-white/5 focus:border-[#1e9f6b] rounded-[28px] pl-16 pr-6 text-xl font-black text-white outline-none transition-all italic tracking-[0.8em] placeholder:text-white/10"
                />
              </div>
            </div>
          </div>

          {/* Holder Name */}
          <div className="space-y-3">
            <label className="text-[11px] font-black text-white/30 uppercase tracking-[0.4em] px-2">
              {t("billing.addCard.fields.holder")}
            </label>
            <div className="relative group">
              <span className="material-symbols-outlined absolute left-6 top-1/2 -translate-y-1/2 text-white/20 text-[24px] transition-all group-focus-within:text-[#1e9f6b]">person</span>
              <input
                value={holder}
                onChange={(e) => setHolder(e.target.value)}
                autoComplete="cc-name"
                placeholder={t("billing.addCard.placeholders.holder")}
                className="w-full h-[76px] bg-white/[0.03] dark:bg-black/20 border-2 border-white/5 focus:border-[#1e9f6b] rounded-[28px] pl-16 pr-6 text-xl font-black text-white outline-none transition-all italic uppercase placeholder:text-white/10"
              />
            </div>
          </div>

          {error && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-5 bg-red-500/10 border border-red-500/20 rounded-3xl flex items-center gap-4 text-red-500"
            >
              <span className="material-symbols-outlined text-[24px]">error</span>
              <p className="text-xs font-black uppercase italic tracking-widest">{error}</p>
            </motion.div>
          )}

          <div className="pt-4">
            <button
              type="submit"
              className="w-full h-[84px] rounded-[32px] bg-[#1e9f6b] hover:bg-[#1ab378] text-white font-black uppercase tracking-[0.3em] text-sm shadow-[0_20px_50px_rgba(30,159,107,0.3)] transition-all flex items-center justify-center gap-4 active:scale-[0.97] group"
            >
              <span>{t("billing.addCard.save")}</span>
              <span className="material-symbols-outlined text-[24px] group-hover:translate-x-1 group-hover:-rotate-12 transition-transform">add_circle</span>
            </button>
          </div>
        </form>
      </div>
    </BaseModal>
  );
}


